import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Grid, Typography } from '@mui/material';
import {
  CheckCircleRounded,
  DescriptionRounded,
  HealingRounded,
  LocalHospitalRounded,
  MedicationRounded,
  PeopleRounded,
  PersonRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import { AuthContext } from 'context/Auth';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import SelectableChoiceCard from 'components/common/SelectableChoiceCard';
import ConnectHealthRecordCard from 'components/dashboard/healthRecords/ConnectHealthRecordCard';
import DocumentsTimeline from 'components/dashboard/healthRecords/DocumentsTimeline';
import ImportHospitalRecordDialog from 'components/dashboard/healthRecords/ImportHospitalRecordDialog';
import DocumentsDialog from 'components/dashboard/healthRecords/DocumentsDialog';
import ShareWithProviderDialog from 'components/dashboard/healthRecords/ShareWithProviderDialog';
import { setupStepPath } from 'components/dashboard/healthRecords/masterChartSetupContext';
import { masterChartPath } from 'constants/masterChartRoutes';
import { isDevEnvironment } from 'utils/isDevEnvironment';

/**
 * `essential` sections are the first-visit setup: what a provider needs before
 * anything else. They come first, unheaded, with the rest under "More history".
 * Labels and descriptions live under hub.categories.<id>, so they follow the language.
 */
const CATEGORIES = [
  {
    id: 'demographics',
    icon: PersonRounded,
    essential: true,
    route: masterChartPath('demographics'),
    isComplete: (hr) => !!(hr?.dateOfBirth && hr?.sex),
  },
  {
    id: 'medications',
    icon: MedicationRounded,
    essential: true,
    route: '/dashboard/medications',
    isComplete: (_, meds) => meds.length > 0,
  },
  {
    id: 'allergies',
    icon: WarningAmberRounded,
    essential: true,
    route: masterChartPath('allergies'),
    isComplete: (hr) => (hr?.allergies?.length ?? 0) > 0,
  },
  {
    id: 'conditions',
    icon: LocalHospitalRounded,
    route: masterChartPath('conditions'),
    isComplete: (hr) => (hr?.pmh?.length ?? 0) > 0,
  },
  {
    id: 'surgical',
    icon: HealingRounded,
    route: masterChartPath('surgical-history'),
    isComplete: (hr) => (hr?.psh?.length ?? 0) > 0,
  },
  {
    id: 'social',
    icon: PeopleRounded,
    route: masterChartPath('other-history'),
    isComplete: (hr) => {
      const other = hr?.other;
      if (!other) return false;
      return Object.values(other).some((v) => v && String(v).trim() !== '');
    },
  },
];

const ESSENTIALS = CATEGORIES.filter((c) => c.essential);
const OPTIONAL = CATEGORIES.filter((c) => !c.essential);

/** What the documents row says while a run is going or suggestions are waiting. */
function documentsRowDescription(t, { busy, suggestionCount }) {
  if (busy) return t('hub.documents.busy');
  if (suggestionCount) return t('hub.documents.waiting', { count: suggestionCount });
  return t('hub.documents.description');
}

/** Optional bold heading over full-width stacked cards. */
const Section = ({ title, children }) => (
  <Box component="section" sx={{ mb: 4 }}>
    {title && (
      <Typography variant="subtitle2" component="h3" fontWeight="bold" sx={{ mb: 1.5 }}>
        {title}
      </Typography>
    )}
    <Grid container spacing={1.5}>
      {children}
    </Grid>
  </Box>
);

/**
 * `openShare` is /masterchart/share, where the menu's "Share with provider" lands: the
 * hub with the share dialog open, the same dialog its button opens.
 *
 * `openImport` is the /masterchart/import route a hospital's link lands on: the hub
 * with "Add a hospital record" already open and the link's ?c= code filled in.
 * Partner API demo only, so there is no button for it on the hub; the dialog
 * opens from that link alone, and only on sandbox.
 */
const HealthRecordsHub = ({ openImport = false, openShare = false }) => {
  const { t } = useTranslation('masterchart');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { userData, medications, medicationsLoading } = useContext(AuthContext);
  const [importOpen, setImportOpen] = useState(openImport && isDevEnvironment());
  const [importedMsg, setImportedMsg] = useState(location.state?.importedMsg ?? null);
  const importedMsgRef = useRef(null);
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(openShare);
  const [documentsStatus, setDocumentsStatus] = useState({ busy: false, suggestionCount: 0 });
  const handleDocumentsStatus = useCallback((status) => setDocumentsStatus(status), []);

  const handleImported = (msg) => {
    importedMsgRef.current = msg;
    setImportedMsg(msg);
  };

  const closeImport = () => {
    setImportOpen(false);
    // Drop the spent code from the address bar, so a refresh doesn't reopen it. The
    // message rides along in case the route change remounts the hub.
    if (openImport) {
      navigate(masterChartPath(), {
        replace: true,
        state: importedMsgRef.current ? { importedMsg: importedMsgRef.current } : undefined,
      });
    }
  };

  const healthRecords = userData?.healthRecords ?? {};
  const meds = medications ?? [];
  const isComplete = (cat) => cat.isComplete(healthRecords, meds);

  // A brand-new chart opens on setup rather than six empty sections. Once the
  // patient leaves setup either way, the flag (and, until the snapshot catches
  // up, the navigation state) keeps them here; setup is not offered again.
  const isNewChart = !!userData && !medicationsLoading && !CATEGORIES.some(isComplete);
  const sendToSetup =
    isNewChart && !openImport && !openShare && !userData.masterChartSetupSeen && !location.state?.setupSeen;
  useEffect(() => {
    if (sendToSetup) navigate(setupStepPath(0), { replace: true });
  }, [sendToSetup, navigate]);

  const openCategory = (cat) =>
    navigate(cat.route, {
      state: cat.id === 'medications' ? { from: 'masterchart' } : undefined,
    });

  // A filled-in section swaps its icon for a check; the text stays the same.
  const renderCards = (cats) =>
    cats.map((cat) => {
      const done = isComplete(cat);
      const Icon = done ? CheckCircleRounded : cat.icon;
      return (
        <Grid item xs={12} key={cat.id}>
          <SelectableChoiceCard
            icon={<Icon />}
            title={t(`hub.categories.${cat.id}.label`)}
            description={t(`hub.categories.${cat.id}.description`)}
            onClick={() => openCategory(cat)}
            alignIcon="center"
          />
        </Grid>
      );
    });

  if (sendToSetup) return null;

  return (
    <div className="inner">
      <DashboardPageHeader
        title="MasterChart"
        subtitle={t('hub.subtitle')}
        actions={
          <Button
            variant="contained"
            onClick={() => setShareOpen(true)}
          >
            {t('hub.share')}
          </Button>
        }
      />

      {importedMsg && (
        <Alert severity="success" onClose={() => setImportedMsg(null)} sx={{ mb: 2 }}>
          {/* The dialogs send { key, values } rather than a sentence, so it follows a language switch. */}
          {typeof importedMsg === 'string' ? importedMsg : t(importedMsg.key, importedMsg.values)}
        </Alert>
      )}

      <Section>
        {renderCards(ESSENTIALS)}
      </Section>

      <Section title={t('hub.moreHistory')}>
        {renderCards(OPTIONAL)}
      </Section>

      <Section title={t('hub.documentsSection')}>
        <Grid item xs={12}>
          <SelectableChoiceCard
            icon={<DescriptionRounded />}
            title={t('hub.documents.label')}
            description={documentsRowDescription(t, documentsStatus)}
            onClick={() => setDocumentsOpen(true)}
            alignIcon="center"
          />
        </Grid>
      </Section>

      <ShareWithProviderDialog
        open={shareOpen}
        onClose={() => {
          setShareOpen(false);
          // The menu's /masterchart/share opened it; leave that address so a refresh
          // doesn't reopen it.
          if (openShare) navigate(masterChartPath(), { replace: true });
        }}
      />

      <DocumentsDialog
        open={documentsOpen}
        onClose={() => setDocumentsOpen(false)}
        onImported={handleImported}
        onStatusChange={handleDocumentsStatus}
      />

      {isDevEnvironment() && (
        <ImportHospitalRecordDialog
          open={importOpen}
          initialCode={openImport ? searchParams.get('c') : ''}
          onClose={closeImport}
          onImported={handleImported}
        />
      )}

      {/* Sandbox-only SMART on FHIR connection; renders nothing elsewhere. */}
      <ConnectHealthRecordCard />

      {/* When the documents are from, with patient-pinned milestones. */}
      <DocumentsTimeline />

      {/* Breathing room so the timeline can be scrolled up off the bottom edge. */}
      <Box aria-hidden sx={{ height: '40vh' }} />
    </div>
  );
};

export default HealthRecordsHub;
